import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, User, ChevronDown, Eye, Plus } from "lucide-react";
import { faviconFor, displayName } from "./listings";
import { goUrl } from "@/api";
import { GhostSeats } from "./GhostSeats";
import { ShareTrigger } from "./ShareCard";
import { listingOutboundHref } from "./shareLinks";
import { takeAmountForRank } from "./rankForAmount";

const Favicon = ({ listing, className }) => {
  const src = listing.favicon_url || listing.favicon || faviconFor(listing.url);
  if (!src) return null;
  return (
    <img
      data-testid="listing-favicon"
      src={src}
      alt=""
      width={16}
      height={16}
      className={`shrink-0 rounded-sm bg-white/10 ${className || ""}`}
    />
  );
};

// Open the listing via /go/:id, which increments the click count server-side and
// 302s to the real url. We deliberately do NOT also POST /api/clicks/:id here —
// that endpoint exists for environments where the 302 navigation can't fire, and
// calling both would double-count. The visible count is bumped optimistically by
// the parent (App) through onOpenListing.
function defaultOpenListing(listing) {
  window.open(goUrl(listing.id), "_blank", "noopener,noreferrer");
}

// ---- List row details: one-line description with expand toggle ----
const RowDetails = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const full = entry.description || "";
  const oneLineText = full.length > 80 ? `${full.slice(0, 80).trimEnd()}…` : full;
  return (
    <div className="mt-0.5">
      <p data-testid="listing-description" className="listing-desc text-[13px] leading-relaxed text-slate-300 sm:text-sm">
        {open ? full : oneLineText}
      </p>
      {full.length > 80 && (
        <button
          type="button"
          data-testid="listing-see-details"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
          className="mono mt-0.5 flex items-center gap-1 text-[10px] tracking-[0.2em] text-gmh-cyan hover:underline"
        >
          {open ? "HIDE" : "SEE DETAILS"}
          <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
};

export const Leaderboard = ({ leaderboard, total, onOpenListing, onClaim, showGhosts = true, board = [] }) => {
  // Well Wishers list starts at #1 — same ranks as the gem podium (not 4+ only).
  // Every paid listing renders (no Load More); empty ranks fill with 004-style
  // ghost rows up to the top 10. No second gem podium inside the list.
  const paidRows = leaderboard;
  const handleOpen = onOpenListing || defaultOpenListing;

  const paidCount = Math.min(paidRows.length, 10);
  const ghostRanks = Array.from({ length: Math.max(0, 10 - paidCount) }, (_, i) => paidCount + i + 1);

  // List rank numerals (004 gold/silver/bronze idea, GMH gems): #1 Amber,
  // #2 Ruby, #3 Emerald — paid and empty. #4+ stay slate. Number only.
  const rankNumeralClass = (rank) =>
    rank === 1
      ? "text-gem-amber"
      : rank === 2
        ? "text-gem-ruby"
        : rank === 3
          ? "text-gem-emerald"
          : "text-slate-500";

  return (
    <section id="leaderboard">
      <div className="mb-6 flex min-w-0 flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2.5 text-xl font-bold tracking-wide text-white sm:text-2xl">
            <Trophy size={20} className="text-gmh-cyan" /> THE WELL WISHERS
          </h2>
          <p className="mono mt-1 text-xs text-slate-500">Top listings accelerating the rescue mission.</p>
        </div>
        <div className="min-w-0 text-right">
          <div className="mono text-[10px] tracking-[0.3em] text-slate-500">TOTAL SECURED</div>
          <div data-testid="total-secured" className="mono text-3xl font-bold text-gmh-orange drop-shadow-[0_0_14px_rgba(255,122,47,0.4)]">
            ${total.toLocaleString()} <span className="text-xs font-normal text-slate-500">USD</span>
          </div>
        </div>
      </div>

      <div
        data-testid="listing-board"
        className="listing-board listing-card rounded-xl border border-[#1c2740] p-3 sm:p-4"
      >
        {/* Ranks 1+: paid rows, then 004-style ghost rows to fill the top 10 */}
        <div data-testid="leaderboard-list" className="flex flex-col gap-2">
          <AnimatePresence initial={false}>
            {paidRows.map((e, i) => {
              const rank = i + 1;
              const takeAmt = takeAmountForRank(board, rank);
              return (
                <motion.div
                  layout
                  key={e.id}
                  id={`listing-${e.id}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid={`leaderboard-row-${rank}`}
                  onClick={() => handleOpen(e)}
                  className={`listing-card row-alt group flex min-w-0 cursor-pointer flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-[#141d34]/60 px-4 py-3 transition-colors hover:bg-[#0e1730] ${e.isYou ? "row-you" : ""}`}
                >
                  <span className={`mono w-8 shrink-0 font-bold ${rankNumeralClass(rank)}`}>#{rank}</span>
                  <div className="flex min-w-0 flex-1 basis-full flex-col sm:basis-48">
                    <div className="flex min-w-0 items-center gap-2">
                      <Favicon listing={e} />
                      {listingOutboundHref(e.url) ? (
                        <a
                          href={listingOutboundHref(e.url)}
                          data-testid="listing-url"
                          className="min-w-0 truncate font-medium text-slate-200 hover:underline"
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            handleOpen(e);
                          }}
                        >
                          {displayName(e)}
                        </a>
                      ) : (
                        <span className="min-w-0 truncate font-medium text-slate-200">{displayName(e)}</span>
                      )}
                      {e.isYou && <User size={12} className="ml-1 inline shrink-0 text-gmh-cyan" />}
                      <ShareTrigger listing={e} rank={rank} />
                    </div>
                    <RowDetails entry={e} />
                  </div>
                  <span data-testid="listing-clicks" className="mono flex items-center gap-1 text-xs text-slate-400"><Eye size={11} /> {e.clicks.toLocaleString()}</span>
                  <span className="mono text-slate-200">${e.amount.toLocaleString()}</span>
                  <button
                    type="button"
                    data-testid="row-take-btn"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      if (onClaim) onClaim(rank);
                    }}
                    className="cta-fuel mono ml-auto inline-flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-[11px] font-bold tracking-wide text-[#2a1000] lg:invisible lg:opacity-0 lg:group-hover:visible lg:group-hover:opacity-100 lg:group-focus-within:visible lg:group-focus-within:opacity-100"
                  >
                    <Plus size={12} /> TAKE #{rank} · ${takeAmt}
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
        {onClaim && showGhosts && ghostRanks.length > 0 && <GhostSeats ranks={ghostRanks} onClaim={onClaim} />}
      </div>
    </section>
  );
};
